import React, { forwardRef } from 'react';
import { Text, Timeline } from '@mantine/core';
import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { PriceChangeRequestHistoryItem } from '../../../codegen/graphql';
import { When } from '../../common';
import PriceChangeRequestEscalationEventsDropdown from './PriceChangeRequestEscalationEventsDropdown';

type TimelineColor =
    | 'blue'
    | 'cyan'
    | 'dark'
    | 'gray'
    | 'green'
    | 'indigo'
    | 'lime'
    | 'orange'
    | 'pink'
    | 'primary'
    | 'red'
    | 'teal'
    | 'violet'
    | 'yellow';

export function formatHistoryItemPreviewSubtitle(
    item: Pick<PriceChangeRequestHistoryItem, 'description' | 'date'>,
): string {
    const description = item.description?.trim() ?? '';
    const relative = item.date ? dayjs(item.date).fromNow() : null;
    if (description && relative) {
        return `${description} • ${relative}`;
    }
    return description || relative || 'No activity yet';
}

function historyStatusIcon(status: string): React.ReactNode {
    if (status.includes('Approved')) {
        return <IconCheck size={12} />;
    }
    if (status.includes('Declined')) {
        return <IconX size={12} />;
    }
    return <IconPlus size={12} />;
}

function historyStatusColor(status: string): TimelineColor {
    if (status.includes('Approved')) {
        return 'primary';
    }
    if (status.includes('Declined')) {
        return 'red';
    }
    return 'gray';
}

export type PriceChangeRequestHistoryTimelineItemProps =
    React.ComponentPropsWithoutRef<typeof Timeline.Item> & {
        timelineItem: PriceChangeRequestHistoryItem;
    };

export const PriceChangeRequestHistoryTimelineItem = forwardRef<
    HTMLDivElement,
    PriceChangeRequestHistoryTimelineItemProps
>(function PriceChangeRequestHistoryTimelineItem(
    { timelineItem, ...timelineItemProps },
    ref,
) {
    const {
        status,
        title,
        description,
        escalationMessage,
        escalationEvents,
        reasonForChange,
        declineReason,
        cashbuildPrice,
        pnlPrice,
        temporaryChangedPrice,
        temporaryCashbuildPrice,
        temporaryPnlPrice,
        temporaryChangeReason,
        note,
        date,
    } = timelineItem;

    return (
        <Timeline.Item
            ref={ref}
            {...timelineItemProps}
            bullet={historyStatusIcon(status)}
            title={title}
            color={historyStatusColor(status)}
        >
            <Text c="dimmed" size="sm">
                {description}
            </Text>
            <When condition={!!escalationMessage}>
                <Text c="red" size="sm">
                    {escalationMessage}
                </Text>
            </When>
            <PriceChangeRequestEscalationEventsDropdown
                events={escalationEvents}
            />
            <When condition={status === 'ApprovedWithTemporaryPrice'}>
                <>
                    <When condition={temporaryCashbuildPrice != null}>
                        <Text size="sm" className="text-[rgba(250,176,5,0.5)]">
                            <strong>Interim Cashbuild Price: </strong>
                            {temporaryCashbuildPrice}
                        </Text>
                    </When>
                    <When condition={!!temporaryPnlPrice}>
                        <Text size="sm" className="text-[rgba(250,176,5,0.5)]">
                            <strong>Interim P&L Price: </strong>
                            {temporaryPnlPrice}
                        </Text>
                    </When>
                    <When
                        condition={
                            temporaryChangedPrice != null &&
                            temporaryCashbuildPrice == null &&
                            temporaryPnlPrice == null
                        }
                    >
                        <Text size="sm" className="text-[rgba(250,176,5,0.5)]">
                            <strong>Interim Price: </strong>
                            {temporaryChangedPrice}
                        </Text>
                    </When>
                </>
            </When>
            <When condition={!!cashbuildPrice}>
                <Text
                    size="sm"
                    className={
                        status === 'Approved'
                            ? 'text-[rgba(18,184,134,0.65)]'
                            : 'text-[rgba(250,176,5,0.5)]'
                    }
                >
                    <strong>Final Cashbuild Price: </strong>
                    {cashbuildPrice}
                </Text>
            </When>
            <When condition={!!pnlPrice}>
                <Text
                    size="sm"
                    className={
                        status === 'Approved'
                            ? 'text-[rgba(18,184,134,0.65)]'
                            : 'text-[rgba(250,176,5,0.5)]'
                    }
                >
                    <strong>Final P&L Price: </strong>
                    {pnlPrice}
                </Text>
            </When>
            <When condition={!!declineReason}>
                <Text c="dimmed" size="sm">
                    <strong>Reason: </strong>
                    {declineReason}
                </Text>
            </When>
            <When condition={!!reasonForChange}>
                <Text c="dimmed" size="sm">
                    <strong>Reason: </strong>
                    {reasonForChange}
                </Text>
            </When>
            <When condition={!!temporaryChangeReason}>
                <Text c="dimmed" size="sm">
                    <strong>Reason: </strong>
                    {temporaryChangeReason}
                </Text>
            </When>
            <When condition={!!note}>
                <Text c="dimmed" size="sm">
                    <strong>Note: </strong>
                    {note}
                </Text>
            </When>
            <When condition={!!date}>
                <Text size="xs" mt={4}>
                    {dayjs(date).fromNow()}
                </Text>
            </When>
        </Timeline.Item>
    );
});
